import { apiGet, apiPost, apiPatch, apiPut, apiDelete, ApiError } from '../lib/apiClient.js';

const BASE = '/internal/admin/api/v1';

export class OperatorAuthError extends Error {
  constructor() {
    super('OPERATOR_AUTH_REQUIRED');
    this.name = 'OperatorAuthError';
  }
}

function rethrow(error: unknown): never {
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    throw new OperatorAuthError();
  }
  throw error;
}

export interface OperatorSession {
  readonly authenticated: boolean;
  readonly actorId?: string;
}

export interface OrganizationCommercial {
  readonly mode: string;
  readonly status: string;
  readonly planDisplayName: string;
  readonly trialStartedAt: string | null;
  readonly trialEndsAt: string | null;
  readonly trialExtendedCount: number;
  readonly seatLimit: number;
  readonly seatUsed: number;
  readonly postTrialBehavior: string | null;
  readonly trialDurationDays: number | null;
  readonly trialSeatLimit: number | null;
}

export interface OrganizationSummary {
  readonly organizationRef: string;
  readonly name: string;
  readonly slug: string;
  readonly lifecycleStatus: string;
  readonly commercial: OrganizationCommercial;
  readonly createdAt: string;
}

export interface OrganizationDetail extends OrganizationSummary {
  readonly ownerLoginIdentifier: string;
}

export interface OperatorPlan {
  readonly planRef: string;
  readonly displayName: string;
  readonly trialEnabled: boolean;
  readonly billingInterval: string;
  readonly basePriceRial: string;
  readonly extraSeatPriceRial: string;
  readonly includedSeats: number;
  readonly trialDurationDays: number | null;
  readonly trialSeatLimit: number | null;
  readonly moduleAllowlist: readonly string[] | null;
  readonly postTrialBehavior: string;
  readonly active: boolean;
}

export interface OperatorAuditEvent {
  readonly occurredAt: string;
  readonly action: string;
  readonly actor: string;
  readonly organizationRef: string | null;
  readonly status: string;
  readonly reason: string | null;
}

export interface OperatorSmsDelivery {
  readonly deliveryRef: string;
  readonly recipientMobile: string;
  readonly templateKey: string;
  readonly provider: string;
  readonly providerMessageId: string | null;
  readonly acceptanceStatus: string;
  readonly errorCode: string | null;
  readonly sentAt: string | null;
  readonly createdAt: string;
  readonly deliveryState: number | null;
  readonly deliveryAt: string | null;
  readonly lastCheckedAt: string | null;
  readonly receiptErrorCode: string | null;
}

export interface AiAssistantInterest {
  readonly requestedAt: string;
  readonly tenantSlug: string;
  readonly displayName: string;
}

export async function operatorLogin(password: string): Promise<void> {
  try {
    await apiPost(`${BASE}/session/login`, { password }, { signal: AbortSignal.timeout(8_000) });
  } catch (error) {
    rethrow(error);
  }
}

export async function operatorLogout(): Promise<void> {
  await apiPost(`${BASE}/session/logout`);
}

export async function getOperatorSession(): Promise<OperatorSession> {
  try {
    return await apiGet<OperatorSession>(`${BASE}/session`, {
      signal: AbortSignal.timeout(8_000),
    });
  } catch {
    return { authenticated: false };
  }
}

export async function listOperatorOrganizations(): Promise<readonly OrganizationSummary[]> {
  try {
    const body = await apiGet<{ organizations: readonly OrganizationSummary[] }>(
      `${BASE}/organizations`,
    );
    return body.organizations;
  } catch (error) {
    rethrow(error);
  }
}

export async function getOperatorOrganization(slug: string): Promise<OrganizationDetail> {
  try {
    return await apiGet<OrganizationDetail>(`${BASE}/organizations/${encodeURIComponent(slug)}`);
  } catch (error) {
    rethrow(error);
  }
}

export async function listOperatorPlans(includeInactive = false): Promise<readonly OperatorPlan[]> {
  try {
    const suffix = includeInactive ? '?includeInactive=true' : '';
    const body = await apiGet<{ plans: readonly OperatorPlan[] }>(`${BASE}/plans${suffix}`);
    return body.plans;
  } catch (error) {
    rethrow(error);
  }
}

export async function createOperatorOrganization(input: {
  organizationName: string;
  slug: string;
  ownerLoginIdentifier: string;
  initialCredential: string;
  planRef: string;
  mfaRequired: boolean;
}): Promise<OrganizationDetail> {
  try {
    return await apiPost<OrganizationDetail>(`${BASE}/organizations`, input);
  } catch (error) {
    rethrow(error);
  }
}

export async function suspendOperatorOrganization(
  slug: string,
  reason: string,
): Promise<OrganizationDetail> {
  try {
    return await apiPost<OrganizationDetail>(
      `${BASE}/organizations/${encodeURIComponent(slug)}/suspend`,
      { reason },
    );
  } catch (error) {
    rethrow(error);
  }
}

export async function reactivateOperatorOrganization(
  slug: string,
  reason: string,
): Promise<OrganizationDetail> {
  try {
    return await apiPost<OrganizationDetail>(
      `${BASE}/organizations/${encodeURIComponent(slug)}/reactivate`,
      { reason },
    );
  } catch (error) {
    rethrow(error);
  }
}

export async function overrideOperatorTrial(
  slug: string,
  action: 'extend' | 'shorten' | 'cancel',
  reason: string,
  adjustmentDays?: number,
): Promise<OrganizationDetail> {
  try {
    return await apiPost<OrganizationDetail>(
      `${BASE}/organizations/${encodeURIComponent(slug)}/trial/${action}`,
      adjustmentDays === undefined ? { reason } : { reason, adjustmentDays },
    );
  } catch (error) {
    rethrow(error);
  }
}

export async function extendOperatorTrial(
  slug: string,
  reason: string,
  adjustmentDays: number,
): Promise<OrganizationDetail> {
  return overrideOperatorTrial(slug, 'extend', reason, adjustmentDays);
}

export async function createOperatorPlan(input: {
  displayName: string;
  billingInterval: string;
  basePriceRial: string;
  extraSeatPriceRial: string;
  includedSeats: number;
  trialEnabled: boolean;
  trialDurationDays: number | null;
  trialSeatLimit: number | null;
  moduleAllowlist: readonly string[] | null;
  postTrialBehavior: string;
}): Promise<OperatorPlan> {
  try {
    return await apiPost<OperatorPlan>(`${BASE}/plans`, input);
  } catch (error) {
    rethrow(error);
  }
}

export async function setOperatorPlanActive(
  planRef: string,
  active: boolean,
): Promise<OperatorPlan> {
  try {
    return await apiPost<OperatorPlan>(
      `${BASE}/plans/${encodeURIComponent(planRef)}/${active ? 'activate' : 'deactivate'}`,
    );
  } catch (error) {
    rethrow(error);
  }
}

export async function listOperatorSmsDeliveries(
  slug: string,
): Promise<readonly OperatorSmsDelivery[]> {
  try {
    const body = await apiGet<{ deliveries: readonly OperatorSmsDelivery[] }>(
      `${BASE}/organizations/${encodeURIComponent(slug)}/sms-deliveries`,
    );
    return body.deliveries;
  } catch (error) {
    rethrow(error);
  }
}

export async function refreshOperatorSmsDelivery(
  slug: string,
  deliveryRef: string,
): Promise<OperatorSmsDelivery> {
  try {
    return await apiPost<OperatorSmsDelivery>(
      `${BASE}/organizations/${encodeURIComponent(slug)}/sms-deliveries/${encodeURIComponent(deliveryRef)}/refresh`,
    );
  } catch (error) {
    rethrow(error);
  }
}

export async function updateOperatorPlan(
  planRef: string,
  input: Partial<{
    displayName: string;
    billingInterval: string;
    basePriceRial: string;
    extraSeatPriceRial: string;
    includedSeats: number;
    trialEnabled: boolean;
    trialDurationDays: number | null;
    trialSeatLimit: number | null;
    moduleAllowlist: readonly string[] | null;
    postTrialBehavior: string;
  }>,
): Promise<OperatorPlan> {
  try {
    return await apiPatch<OperatorPlan>(`${BASE}/plans/${encodeURIComponent(planRef)}`, input);
  } catch (error) {
    rethrow(error);
  }
}

export async function listOperatorAiAssistantInterest(): Promise<readonly AiAssistantInterest[]> {
  try {
    const body = await apiGet<{ signups: readonly AiAssistantInterest[] }>(
      `${BASE}/ai-assistant-interest`,
    );
    return body.signups;
  } catch (error) {
    rethrow(error);
  }
}

export async function listOperatorAudit(query: {
  organizationRef?: string;
  action?: string;
  actor?: string;
  from?: string;
  to?: string;
}): Promise<readonly OperatorAuditEvent[]> {
  try {
    const params = new URLSearchParams();
    if (query.organizationRef) params.set('organizationRef', query.organizationRef);
    if (query.action) params.set('action', query.action);
    if (query.actor) params.set('actor', query.actor);
    if (query.from) params.set('from', query.from);
    if (query.to) params.set('to', query.to);
    const suffix = params.size > 0 ? `?${params.toString()}` : '';
    const body = await apiGet<{ events: readonly OperatorAuditEvent[] }>(`${BASE}/audit${suffix}`);
    return body.events;
  } catch (error) {
    rethrow(error);
  }
}

export interface TenantUserSummary {
  readonly accountId: string;
  readonly displayName: string;
  /** Preset avatar id (`a1`…`a10`), if the person picked one. */
  readonly avatarPreset: string | null;
  /** An uploaded photo exists; fetch it from {@link operatorUserAvatarUrl}. */
  readonly hasAvatarPhoto: boolean;
  /** The accent color the person chose (`indigo`, `emerald`, `ruby`, `amber`, `violet`, `slate`). */
  readonly brandColor: string | null;
  /** Gregorian `YYYY-MM-DD`; display through `displayJalaliDate`. */
  readonly birthdate: string | null;
  readonly contactIdentifier: string;
  readonly status: string;
  readonly roleTitle: string;
  readonly createdAt: string;
}

export interface TenantIssueSummary {
  readonly id: string;
  readonly category: 'sms' | 'lifecycle' | 'operator' | 'audit';
  readonly level: 'error' | 'warning' | 'info';
  readonly title: string;
  readonly detail: string;
  readonly occurredAt: string;
}

export interface MessengerNotificationConfigStatus {
  readonly telegramConfigured: boolean;
  readonly telegramMode?: 'relay' | 'direct' | 'none';
  readonly telegramEnabled?: boolean;
  readonly notifySignup?: boolean;
  readonly notifyEstimate?: boolean;
  readonly targetChatId?: string | null;
  readonly relayUrl?: string | null;
  readonly timeoutMs?: number;
  readonly baleConfigured: boolean;
  readonly webhookConfigured: boolean;
}

export interface MessengerNotificationResult {
  readonly telegram?: { success: boolean; error?: string };
  readonly bale?: { success: boolean; error?: string };
  readonly webhook?: { success: boolean; error?: string };
}

/** Image URLs for `<img>`: same-origin, authorised by the operator session cookie. */
export const operatorOrgLogoUrl = (slug: string): string =>
  `${BASE}/organizations/${encodeURIComponent(slug)}/logo`;
export const operatorUserAvatarUrl = (slug: string, accountId: string): string =>
  `${BASE}/organizations/${encodeURIComponent(slug)}/users/${encodeURIComponent(accountId)}/avatar`;

export async function listOrganizationUsers(slug: string): Promise<readonly TenantUserSummary[]> {
  try {
    const body = await apiGet<{ users: readonly TenantUserSummary[] }>(
      `${BASE}/organizations/${encodeURIComponent(slug)}/users`,
    );
    return body.users;
  } catch (error) {
    rethrow(error);
  }
}

export async function listOrganizationErrors(slug: string): Promise<readonly TenantIssueSummary[]> {
  try {
    const body = await apiGet<{ issues: readonly TenantIssueSummary[] }>(
      `${BASE}/organizations/${encodeURIComponent(slug)}/errors`,
    );
    return body.issues;
  } catch (error) {
    rethrow(error);
  }
}

export async function changeOrganizationPlan(
  slug: string,
  planRef: string,
  reason: string,
  seatLimit?: number,
): Promise<OrganizationDetail> {
  try {
    return await apiPost<OrganizationDetail>(
      `${BASE}/organizations/${encodeURIComponent(slug)}/change-plan`,
      seatLimit !== undefined ? { planRef, reason, seatLimit } : { planRef, reason },
    );
  } catch (error) {
    rethrow(error);
  }
}

export async function operatorSupportLogin(
  slug: string,
): Promise<{ redirectTo: string; success: boolean }> {
  try {
    return await apiPost<{ redirectTo: string; success: boolean }>(
      `${BASE}/organizations/${encodeURIComponent(slug)}/support-login`,
      {},
    );
  } catch (error) {
    rethrow(error);
  }
}

export async function getMessengerConfigStatus(): Promise<MessengerNotificationConfigStatus> {
  try {
    const body = await apiGet<{ status: MessengerNotificationConfigStatus }>(
      `${BASE}/notifications/status`,
    );
    return body.status;
  } catch (error) {
    rethrow(error);
  }
}

export async function testMessengerNotification(
  text?: string,
  type: 'signup' | 'estimate' = 'signup',
): Promise<MessengerNotificationResult> {
  try {
    const body = await apiPost<{ result: MessengerNotificationResult }>(
      `${BASE}/notifications/test-messenger`,
      text ? { text, type } : { type },
    );
    return body.result;
  } catch (error) {
    rethrow(error);
  }
}

export interface OperatorBillingOrder {
  readonly orderId: string;
  readonly slug: string;
  readonly organizationName: string;
  readonly ownerName: string;
  readonly ownerContactLabel: string;
  readonly planId: string;
  readonly planName: string | null;
  readonly usersCount: number;
  readonly durationMonths: number;
  readonly amountRial: string;
  readonly amountToman: string;
  readonly status: 'pending' | 'paid' | 'failed' | 'provisioned';
  readonly authority: string | null;
  readonly refId: string | null;
  readonly cardPan: string | null;
  readonly failureReason: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface OperatorBillingOrdersSummary {
  readonly totalOrders: number;
  readonly paidOrders: number;
  readonly pendingOrders: number;
  readonly failedOrders: number;
  readonly totalPaidAmountRial: string;
  readonly totalPaidAmountToman: string;
}

export interface OperatorBillingOrdersResult {
  readonly orders: readonly OperatorBillingOrder[];
  readonly summary: OperatorBillingOrdersSummary;
}

export async function listBillingOrders(filter?: {
  slug?: string | undefined;
  status?: string | undefined;
}): Promise<OperatorBillingOrdersResult> {
  try {
    const params = new URLSearchParams();
    if (filter?.slug) params.set('slug', filter.slug);
    if (filter?.status) params.set('status', filter.status);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return await apiGet<OperatorBillingOrdersResult>(`${BASE}/billing/orders${qs}`);
  } catch (error) {
    rethrow(error);
  }
}

export async function listOrganizationBillingOrders(
  slug: string,
): Promise<OperatorBillingOrdersResult> {
  try {
    return await apiGet<OperatorBillingOrdersResult>(
      `${BASE}/organizations/${encodeURIComponent(slug)}/billing-orders`,
    );
  } catch (error) {
    rethrow(error);
  }
}

export { apiPatch };

export interface UpcomingBirthday {
  readonly organizationRef: string;
  readonly organizationName: string;
  readonly accountId: string;
  readonly displayName: string;
  readonly roleTitle: string;
  readonly avatarPreset: string | null;
  readonly hasAvatarPhoto: boolean;
  readonly brandColor: string | null;
  readonly birthdate: string;
  /** Gregorian date of the next birthday (celebrated on the Jalali calendar). */
  readonly nextBirthday: string;
  readonly daysUntil: number;
  /** Age reached on that day, in Jalali years. */
  readonly turningAge: number;
}

/** Birthdays within `days` across every organization and member (the read is audited). */
export async function listUpcomingBirthdays(days: number): Promise<readonly UpcomingBirthday[]> {
  try {
    const body = await apiGet<{ birthdays: readonly UpcomingBirthday[] }>(
      `${BASE}/birthdays?days=${encodeURIComponent(String(days))}`,
    );
    return body.birthdays;
  } catch (error) {
    rethrow(error);
  }
}

export interface ShortLinkCheck {
  readonly id: string;
  readonly pass: boolean;
  readonly detail: string;
  /** Informational only: shown, never blocks. */
  readonly advisory?: boolean;
}

export interface RehearsalStep {
  readonly id: 'change' | 'live_after_change' | 'restore' | 'live_after_restore';
  readonly pass: boolean;
  readonly detail: string;
}

export interface OperatorShortLink {
  readonly code: string;
  /** Internal `/t/…` path or a canonical external `https://…` URL. */
  readonly targetPath: string;
  readonly note: string | null;
  readonly status: 'active' | 'disabled';
  /** Quote this when editing: a stale edit is refused instead of silently overwriting. */
  readonly revision: number;
  readonly verifiedAt: string | null;
  readonly verification: {
    readonly pass: boolean;
    readonly checkedAt: string;
    readonly revision: number;
    readonly checks: readonly ShortLinkCheck[];
  } | null;
  readonly rehearsalAt: string | null;
  readonly rehearsal: {
    readonly pass: boolean;
    readonly checkedAt: string;
    readonly steps: readonly RehearsalStep[];
  } | null;
  readonly firstReleasedAt: string | null;
  readonly printApprovedAt: string | null;
  readonly printApprovedBy: string | null;
  readonly updatedAt: string;
  readonly updatedBy: string;
  readonly destination: {
    readonly kind: 'internal' | 'external';
    readonly host: string | null;
    /** `null` for internal destinations. */
    readonly hostAllowed: boolean | null;
  };
  /** Advisory only. */
  readonly tenant: { readonly exists: boolean; readonly status: string | null };
  readonly state: {
    readonly redirectConfigured: boolean;
    readonly destination: 'unverified' | 'verified' | 'stale' | 'failed';
    readonly rehearsal: 'not_done' | 'passed' | 'failed';
    readonly print: 'not_approved' | 'approved' | 'stale';
    readonly printReady: boolean;
    readonly blockers: readonly string[];
  };
}

export interface OperatorShortLinkHost {
  readonly host: string;
  readonly note: string | null;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly usedBy: readonly string[];
}

export interface OperatorShortLinksResult {
  /** `APP_BASE_URL` of this deployment. */
  readonly publicBaseUrl: string;
  /** How the PUBLIC route is currently answering (snapshot health). Not the authoritative state. */
  readonly resolver: {
    readonly snapshotCodes: number;
    readonly lastFullRefreshAt: string | null;
    readonly lastRefreshError: string | null;
    readonly lkgServed: number;
    readonly unavailableServed: number;
    readonly lkgMaxAgeSeconds: number;
  };
  readonly shortLinks: readonly OperatorShortLink[];
}

const slEnc = (code: string) => encodeURIComponent(code);

/** Throws (ApiError, status 503 when the database cannot answer). Never returns fallback data. */
export async function listOperatorShortLinks(): Promise<OperatorShortLinksResult> {
  try {
    return await apiGet<OperatorShortLinksResult>(`${BASE}/short-links`);
  } catch (error) {
    rethrow(error);
  }
}

/** Create a NEW code. An existing code is a 409 — creating can never reassign a printed code. */
export async function createOperatorShortLink(body: {
  code: string;
  destination: string;
  note: string | null;
  status: 'active' | 'disabled';
}): Promise<OperatorShortLink> {
  try {
    return (await apiPost<{ shortLink: OperatorShortLink }>(`${BASE}/short-links`, body)).shortLink;
  } catch (error) {
    rethrow(error);
  }
}

/** Change an EXISTING code's destination/status/note. The code itself can never change. */
export async function updateOperatorShortLink(
  code: string,
  body: {
    destination: string;
    note: string | null;
    status: 'active' | 'disabled';
    expectedRevision: number;
  },
): Promise<OperatorShortLink> {
  try {
    return (
      await apiPut<{ shortLink: OperatorShortLink }>(`${BASE}/short-links/${slEnc(code)}`, body)
    ).shortLink;
  } catch (error) {
    rethrow(error);
  }
}

export async function listOperatorShortLinkHosts(): Promise<readonly OperatorShortLinkHost[]> {
  try {
    return (await apiGet<{ hosts: readonly OperatorShortLinkHost[] }>(`${BASE}/short-link-hosts`))
      .hosts;
  } catch (error) {
    rethrow(error);
  }
}

export async function addOperatorShortLinkHost(host: string, note: string | null): Promise<void> {
  try {
    await apiPut(`${BASE}/short-link-hosts/${encodeURIComponent(host)}`, { note });
  } catch (error) {
    rethrow(error);
  }
}

/** Refused (409) while a code still points at the host. */
export async function removeOperatorShortLinkHost(host: string): Promise<void> {
  try {
    await apiDelete(`${BASE}/short-link-hosts/${encodeURIComponent(host)}`);
  } catch (error) {
    rethrow(error);
  }
}

export async function verifyOperatorShortLink(code: string): Promise<OperatorShortLink> {
  try {
    return (
      await apiPost<{ shortLink: OperatorShortLink }>(
        `${BASE}/short-links/${slEnc(code)}/verify`,
        {},
      )
    ).shortLink;
  } catch (error) {
    rethrow(error);
  }
}

/** Proves on this deployment that the destination can be changed and restored (refused after the first print release). */
export async function rehearseOperatorShortLink(code: string): Promise<OperatorShortLink> {
  try {
    return (
      await apiPost<{ shortLink: OperatorShortLink }>(
        `${BASE}/short-links/${slEnc(code)}/rehearse`,
        {},
      )
    ).shortLink;
  } catch (error) {
    rethrow(error);
  }
}

/** Refused with 409 (details = the failing checks) unless the deployed short URL verifies now and the rehearsal passed. */
export async function approveOperatorShortLinkPrint(code: string): Promise<OperatorShortLink> {
  try {
    return (
      await apiPost<{ shortLink: OperatorShortLink }>(
        `${BASE}/short-links/${slEnc(code)}/approve-print`,
        {},
      )
    ).shortLink;
  } catch (error) {
    rethrow(error);
  }
}

/** Server re-checks live, records the release and returns the ONLY address a print asset may encode. */
export async function releaseOperatorShortLinkPrintAssets(
  code: string,
): Promise<{ code: string; revision: number; url: string }> {
  try {
    return await apiPost<{ code: string; revision: number; url: string }>(
      `${BASE}/short-links/${slEnc(code)}/print-assets`,
      {},
    );
  } catch (error) {
    rethrow(error);
  }
}
